/*
  Tabs.ts

  Utility functions to set up tabbed views

  Copyright (C) 2024 Miðeind ehf.
  Author: Vilhjalmur Thorsteinsson

  The Creative Commons Attribution-NonCommercial 4.0
  International Public License (CC-BY-NC 4.0) applies to this software.
  For further information, see https://github.com/mideind/Netskrafl

*/

import { IView } from "./types";
import { m } from "./mithril";
import { getUrlVars } from "./util";

const ROUTE_PREFIX = "/page#!";
const ROUTE_PREFIX_LEN = ROUTE_PREFIX.length;

export type TabVnode = m.VnodeDOM<
  Record<string, any>,
  {
    selected: number;
    lis: HTMLElement[];
    ids: string[];
  }
>;

export function makeTabs(view: IView, id: string, createFunc: ((vnode: TabVnode) => void) | undefined, wireHrefs: boolean, vnode: TabVnode) {
  // When the tabs are displayed for the first time, wire'em up
  let tabdiv = document.getElementById(id);
  if (!tabdiv)
    return;
  // Add bunch of jQueryUI compatible classes
  tabdiv.setAttribute("class", "ui-tabs ui-widget ui-widget-content ui-corner-all");
  const tabul = document.querySelector("#" + id + " > ul");
  if (tabul) {
    tabul.setAttribute("class", "ui-tabs-nav ui-helper-reset ui-helper-clearfix ui-widget-header ui-corner-all");
    tabul.setAttribute("role", "tablist");
  }
  let tablist = document.querySelectorAll("#" + id + " > ul > li > a") as NodeListOf<HTMLElement>;
  let tabitems = document.querySelectorAll("#" + id + " > ul > li") as NodeListOf<HTMLElement>;
  let ids: string[] = [];
  let lis: HTMLElement[] = []; // The <li> elements
  // Iterate over the <a> elements inside the <li> elements inside the <ul>
  for (let i = 0; i < tablist.length; i++) {
    const href = tablist[i].getAttribute("href");
    if (!href) continue;
    ids.push(href.slice(1));
    // Decorate the <a> elements
    tablist[i].onclick = (ev) => { selectTab(vnode, i); ev.preventDefault(); };
    tablist[i].removeAttribute("href");
    tablist[i].setAttribute("class", "ui-tabs-anchor sp"); // Single-page marker
    tablist[i].setAttribute("role", "presentation");
    // Also decorate the <li> elements
    lis.push(tabitems[i]);
    tabitems[i].setAttribute("class", "ui-state-default ui-corner-top");
    tabitems[i].setAttribute("role", "tab");
    tabitems[i].onmouseover = (ev) => {
      (ev.currentTarget as HTMLElement).classList.toggle("ui-state-hover", true);
    };
    tabitems[i].onmouseout = (ev) => {
      (ev.currentTarget as HTMLElement).classList.toggle("ui-state-hover", false);
    };
    // Find the tab's content <div>
    const tabcontent = document.getElementById(ids[i]);
    // Decorate it
    if (tabcontent) {
      tabcontent.setAttribute("class", "ui-tabs-panel ui-widget-content ui-corner-bottom");
      tabcontent.setAttribute("role", "tabpanel");
    }
  }
  // Save the list of tab identifiers
  vnode.state.ids = ids;
  // Save the list of <li> elements
  vnode.state.lis = lis;
  // Select the first tab by default
  vnode.state.selected = 0;
  if (wireHrefs) {
    // Wire all hrefs that point to single-page URLs
    const model = view.model;
    const clickURL = (ev: Event, href: string) => {
      let uri = href.slice(ROUTE_PREFIX_LEN); // Cut the /page#!/ prefix off the route
      let qix = uri.indexOf("?");
      let route = (qix >= 0) ? uri.slice(0, qix) : uri;
      let qparams = uri.slice(route.length + 1);
      let params = qparams.length ? getUrlVars(qparams) : {};
      m.route.set(route, params);
      if (window.history)
        window.history.pushState({}, "", href); // Enable the back button
      ev.preventDefault();
    };
    const clickUserPrefs = (ev: Event) => {
      if (model?.state?.netskraflUserId)
        // Don't show the userprefs if no user logged in
        view.pushDialog("userprefs");
      ev.preventDefault();
    };
    const clickTwoLetter = (ev: Event) => {
      selectTab(vnode, 2); // Select tab number 2
      ev.preventDefault();
    };
    const clickNewBag = (ev: Event) => {
      selectTab(vnode, 3); // Select tab number 3
      ev.preventDefault();
    };
    let anchors = tabdiv.querySelectorAll("a");
    for (let i = 0; i < anchors.length; i++) {
      let a = anchors[i];
      let href = a.getAttribute("href");
      if (href && href.slice(0, ROUTE_PREFIX_LEN) == ROUTE_PREFIX) {
        // Single-page URL: wire it up (as if it had had an m.route.Link on it)
        a.onclick = (ev) => clickURL(ev, href);
      }
      else
        if (href && href === "$$userprefs$$") {
          // Special marker indicating that this link invokes
          // a user preference dialog
          a.onclick = clickUserPrefs;
        }
        else
          if (href && href === "$$twoletter$$") {
            // Special marker indicating that this link invokes
            // the two-letter word list or the opponents tab
            a.onclick = clickTwoLetter;
          }
          else
            if (href && href === "$$newbag$$") {
              // Special marker indicating that this link invokes
              // the explanation of the new bag
              a.onclick = clickNewBag;
            }
    }
  }
  // If a createFunc was specified, run it now
  if (createFunc)
    createFunc(vnode);
  // Finally, make the default tab visible and hide the others
  updateTabVisibility(vnode);
}

function updateTabVisibility(vnode: TabVnode) {
  // Shows the tab that is currently selected,
  // i.e. the one whose index is in vnode.state.selected
  const selected: number = vnode.state.selected;
  const lis = vnode.state.lis;
  vnode.state.ids.map((id: string, i: number) => {
    document.getElementById(id)?.setAttribute("style", "display: " +
      (i == selected ? "block" : "none"));
    lis[i].classList.toggle("ui-tabs-active", i === selected);
    lis[i].classList.toggle("ui-state-active", i === selected);
  }
  );
}

export function selectTab(vnode: TabVnode, i: number) {
  // Selects the tab with the given index under the tab control vnode
  vnode.state.selected = i;
  updateTabVisibility(vnode);
}

export function updateSelection(vnode: TabVnode) {
  // Select a tab according to the ?tab= query parameter in the current route
  var tab = m.route.param("tab");
  if (tab !== undefined)
    selectTab(vnode, parseInt(tab) || 0);
}
